import React from "react";
import { formatMessage } from "lib/globalize-util";
import { useFetchMerchantBrandPortals } from "containers/folders/hooks/merchants";
import { DATATEST } from "constants/integration";
import { MANAGE_ACCESS_DIALOG_MAX_WIDTH, TabValues } from "containers/folders/utils/constants";
import { ItemKind } from "containers/folders/types/base";
import {
    Dialog,
    DialogType,
    IDialogStyles,
    Theme,
    Fabric,
    IPivotStyles,
    initializeIcons,
    Pivot,
    PivotItem,
} from "@nuorder/components";
import { CollectionType } from "containers/folders/types/assortment";
import { DialogTitle } from "./dialog-title";
import { nameExtRemover } from "containers/folders/utils/space-item";
import { ManageAccessComponent } from "./manage-access-component";
import { useFlags } from "launchdarkly-react-client-sdk";
import { hasVerticalAssortmentAccess } from "containers/folders/utils/vertical-assortments";

interface IAssortmentManageAccessPanelProps {
    open: boolean;
    onDismiss: () => void;
    driveItemData: nuorder.folder.v1.Item;
    merchantId: string;
    isRollupMode: boolean;
    brand?: any;
    me: any;
}

initializeIcons();

export const AssortmentManageAccessDialog: React.FC<IAssortmentManageAccessPanelProps> = ({
    open,
    driveItemData,
    merchantId,
    onDismiss,
    isRollupMode,
    brand,
    me,
}) => {
    const { verticalAssortmentsEnabled } = useFlags();

    const isVerticalAssortmentEnabled = React.useMemo(
        () => !!brand && hasVerticalAssortmentAccess(me, brand, verticalAssortmentsEnabled),
        [brand, verticalAssortmentsEnabled, me]
    );

    const getBrandPortalId = (item: nuorder.folder.v1.Item) => {
        let brandPortalId = undefined;
        if (item?.kind === ItemKind.CollectionDocument && item?.collection_facet?.brand_portal_id?.length) {
            brandPortalId = item?.collection_facet?.brand_portal_id;
        }
        return brandPortalId;
    };
    const externalAssortmentBrandPortalId = !isVerticalAssortmentEnabled && getBrandPortalId(driveItemData);
    const {
        data: externalBrandPortal,
        isLoading: isExternalBrandPortalLoading,
        error: externalBrandPortalError,
    } = useFetchMerchantBrandPortals(externalAssortmentBrandPortalId, {
        enabled: !!driveItemData && open && externalAssortmentBrandPortalId,
    });
    const panelStyles = React.useMemo<Partial<IDialogStyles>>(
        () => ({
            main: {
                maxWidth: `${MANAGE_ACCESS_DIALOG_MAX_WIDTH}px !important`,
                width: "50vw !important",
                " [data-icon-name='Cancel']": {
                    fontWeight: "bold",
                },
                maxHeight: "80vh !important",
            },
            content: {
                padding: 0,
            },
        }),
        []
    );
    const pivotStyles = React.useMemo<Partial<IPivotStyles>>(
        () => ({
            root: { display: `${externalAssortmentBrandPortalId ? "flex" : "none"}`, justifyContent: "space-around" },
            link: {
                flex: "1 1 auto",
            },
            linkIsSelected: {
                flex: "1 1 auto",
            },
            text: {
                fontSize: "15px",
                fontWeight: "bold",
            },
        }),
        [externalAssortmentBrandPortalId]
    );
    const dialogContentProps = {
        type: DialogType.normal,
        title: (
            <DialogTitle
                iconName="users24"
                mainTitle={isRollupMode ? formatMessage("Share Rollup") : formatMessage("Share Assortment")}
                subTitle={nameExtRemover(driveItemData?.name, driveItemData?.kind as ItemKind)}
            />
        ),
        titleProps: { className: DATATEST.FOLDERS.GRANT_ACCESS.DIALOG_TITLE },
        closeButtonAriaLabel: formatMessage("Close"),
        subText: "",
        showCloseButton: true,
        className: DATATEST.FOLDERS.GRANT_ACCESS.DIALOG,
    };
    const flags = useFlags();
    const isCloseoutBrandAssortment =
        flags.closeoutAssortment && driveItemData?.collection_facet?.collection_type === CollectionType.BrandStandard;

    return (
        <Theme>
            <Fabric>
                <Dialog
                    styles={panelStyles}
                    hidden={!open}
                    onDismiss={onDismiss}
                    closeButtonAriaLabel={formatMessage("Close")}
                    dialogContentProps={dialogContentProps}
                >
                    {externalAssortmentBrandPortalId ? (
                        <Pivot
                            styles={pivotStyles}
                            data-test={DATATEST.FOLDERS.ASSORTMENT.GRANT_ACCESS.PIVOT}
                            aria-label={formatMessage("Manage Access Pivot")}
                        >
                            <PivotItem
                                headerText={formatMessage("Share Internally")}
                                itemKey={TabValues.Internally}
                                data-test={DATATEST.FOLDERS.ASSORTMENT.GRANT_ACCESS.INTERNALLY_TAB}
                            >
                                <ManageAccessComponent
                                    sharedItemMerchantId={driveItemData?.collection_facet?.merchant_id}
                                    selectedItemId={driveItemData.id}
                                    merchantId={merchantId}
                                    onDialogDismiss={onDismiss}
                                />
                            </PivotItem>
                            {isCloseoutBrandAssortment ? (
                                <PivotItem
                                    headerText={formatMessage("Share with Buyers")}
                                    itemKey={TabValues.Buyers}
                                    data-test={DATATEST.FOLDERS.ASSORTMENT.GRANT_ACCESS.BUYER_TAB}
                                >
                                    <ManageAccessComponent
                                        closeoutTargetMerchantId={driveItemData?.collection_facet?.target_merchant_id}
                                        selectedItemId={driveItemData.id}
                                        merchantId={merchantId}
                                        onDialogDismiss={onDismiss}
                                        isCloseoutBrandAssortment={isCloseoutBrandAssortment}
                                    />
                                </PivotItem>
                            ) : (
                                <PivotItem
                                    headerText={formatMessage("Share with Brand")}
                                    itemKey={TabValues.Brands}
                                    data-test={DATATEST.FOLDERS.ASSORTMENT.GRANT_ACCESS.BRAND_TAB}
                                >
                                    <ManageAccessComponent
                                        selectedItemId={driveItemData.id}
                                        merchantId={merchantId}
                                        sharedItemMerchantId={driveItemData?.collection_facet?.merchant_id}
                                        externalContactMerchantId={externalBrandPortal?.merchant_id}
                                        externalBrandName={driveItemData?.collection_facet?.brand_portal_name}
                                        isExternalBrandPortalLoading={isExternalBrandPortalLoading}
                                        externalBrandPortalErrorMessage={(externalBrandPortalError as any)?.message}
                                        onDialogDismiss={onDismiss}
                                    />
                                </PivotItem>
                            )}
                        </Pivot>
                    ) : (
                        <ManageAccessComponent
                            selectedItemId={driveItemData.id}
                            merchantId={merchantId}
                            sharedItemMerchantId={driveItemData?.collection_facet?.merchant_id}
                            onDialogDismiss={onDismiss}
                        />
                    )}
                </Dialog>
            </Fabric>
        </Theme>
    );
};
